import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core'
import { DeleteOutline as DeleteIcon } from '@material-ui/icons'
import {
    ADMIN_GET_MOCK_INTEGRATION,
    ADMIN_GET_TENANT_SPIDS,
    ADMIN_GET_TENANT_SURVEYS,
    ADMIN_ASSIGN_SURVEY,
    ADMIN_SAVE_MOCK_DATA,
    ADMIN_SAVE_MOCK_INTEGRATION,
} from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import ConfirmActionDialog from './ConfirmActionDialog'
import { generateMockEmployee } from './mockCharacters'
import { SpidStatusTable } from './SpidStatusTable'

const EMPTY_WORKDAY = { datamap: {}, locationmap: {} }

function entriesFromMap(map) {
    return Object.entries(map || {}).map(([key, value]) => ({ key, value }))
}

function mapFromEntries(entries) {
    const result = {}
    entries.forEach(({ key, value }) => {
        const k = (key || '').trim()
        if (k) result[k] = (value || '').trim()
    })
    return result
}

function datamapKeys(datamap) {
    return Object.keys(datamap || {}).sort()
}

function emptyRow(datamap) {
    const row = {}
    datamapKeys(datamap).forEach(k => { row[k] = '' })
    return row
}

function ScheduleReadOnly({ schedule }) {
    if (!schedule) return null
    const fields = [
        ['Scheduled', schedule.integration_scheduled ? 'Yes' : 'No'],
        ['Cron', schedule.integration_cron || '—'],
        ['Interval', schedule.integration_interval || '—'],
        ['Last success', schedule.last_integration_success_at || '—'],
        ['Last attempt', schedule.last_integration_attempt_at || '—'],
        ['Last failure', schedule.last_integration_failure_at || '—'],
    ]
    return (
        <>
            <div className="sa-mock-banner sa-mock-banner--info">
                Schedule is configured in the client app deployment
                ({' '}
                <code>INTEGRATION_CRON</code> / <code>INTEGRATION_INTERVAL</code>
                ). Values below are reported on heartbeat. Restart the client after changing .env.
            </div>
            <div className="sa-mock-schedule-grid">
                {fields.map(([label, value]) => (
                    <div key={label} className="sa-mock-schedule-field">
                        <div className="sa-mock-schedule-label">{label}</div>
                        <div className="sa-mock-schedule-value">{value}</div>
                    </div>
                ))}
            </div>
        </>
    )
}

function KeyValueEditor({ title, description, entries, onChange, valueFieldType, locationOptions }) {
    return (
        <>
            <div className="sa-mock-section-head">
                <Typography variant="subtitle2">{title}</Typography>
                <span className="sa-mock-chip">Editable</span>
            </div>
            {description && (
                <Typography variant="body2" color="textSecondary" className="sa-mock-section-desc">
                    {description}
                </Typography>
            )}
            <div className="sa-mock-kv-wrap">
                <table className="sa-mock-kv-table">
                    <thead>
                        <tr>
                            <th>{valueFieldType === 'location' ? 'Workday location' : 'Workday column'}</th>
                            <th>{valueFieldType === 'location' ? 'Tenant location id' : 'Mapped label'}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="sa-mock-empty">No entries yet.</td>
                            </tr>
                        ) : entries.map((entry, idx) => (
                            <tr key={idx}>
                                <td>
                                    <TextField
                                        value={entry.key}
                                        onChange={e => {
                                            const next = entries.slice()
                                            next[idx] = { ...next[idx], key: e.target.value }
                                            onChange(next)
                                        }}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                                    />
                                </td>
                                <td>
                                    {valueFieldType === 'location' ? (
                                        <TextField
                                            select
                                            value={entry.value}
                                            onChange={e => {
                                                const next = entries.slice()
                                                next[idx] = { ...next[idx], value: e.target.value }
                                                onChange(next)
                                            }}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        >
                                            <MenuItem value="">— select —</MenuItem>
                                            {locationOptions.map(loc => (
                                                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                            ))}
                                        </TextField>
                                    ) : (
                                        <TextField
                                            value={entry.value}
                                            onChange={e => {
                                                const next = entries.slice()
                                                next[idx] = { ...next[idx], value: e.target.value }
                                                onChange(next)
                                            }}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                                        />
                                    )}
                                </td>
                                <td className="sa-mock-kv-actions">
                                    <IconButton size="small" onClick={() => onChange(entries.filter((_, i) => i !== idx))}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    )
}

function MockRowsEditor({ rows, columns, onChange }) {
    return (
        <div className="sa-mock-rows-wrap">
            <table className="sa-mock-rows-table">
                <thead>
                    <tr>
                        {columns.map(col => <th key={col}>{col}</th>)}
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length + 1} className="sa-mock-empty">
                                No mock employees. Add a row or import JSON.
                            </td>
                        </tr>
                    ) : rows.map((row, ri) => (
                        <tr key={ri}>
                            {columns.map(col => (
                                <td key={col}>
                                    <input
                                        type="text"
                                        value={row[col] || ''}
                                        onChange={e => {
                                            const next = rows.map((r, i) =>
                                                i === ri ? { ...r, [col]: e.target.value } : r
                                            )
                                            onChange(next)
                                        }}
                                    />
                                </td>
                            ))}
                            <td className="sa-mock-kv-actions">
                                <IconButton
                                    size="small"
                                    onClick={() => onChange(rows.filter((_, i) => i !== ri))}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function PreflightBanner({ schedule, tenantLocations, tenantId }) {
    const checks = []

    // Integration scheduled
    const hasSchedule = schedule?.integration_scheduled
    const interval = schedule?.integration_interval || schedule?.integration_cron
    if (!hasSchedule) {
        checks.push({ level: 'warn', msg: 'Integration is not scheduled. Set INTEGRATION_INTERVAL in the client app environment.' })
    } else if (interval) {
        checks.push({ level: 'ok', msg: `Integration runs every ${interval}.` })
    }

    // Last run
    const lastSuccess = schedule?.last_integration_success_at
    const lastFailure = schedule?.last_integration_failure_at
    if (lastSuccess) {
        checks.push({ level: 'ok', msg: `Last successful integration: ${new Date(lastSuccess).toLocaleString()}.` })
    }
    if (lastFailure) {
        checks.push({ level: 'warn', msg: `Last integration failure: ${new Date(lastFailure).toLocaleString()}.` })
    }
    if (!lastSuccess && !lastFailure) {
        checks.push({ level: 'info', msg: 'No integration runs recorded yet.' })
    }

    // Locations
    if (!tenantLocations || tenantLocations.length === 0) {
        checks.push({ level: 'warn', msg: 'No locations configured for this tenant. Location mapping may fail.' })
    }

    // Mock Workday URL helper
    const mockUrl = `https://signoffapi.demo.ourdataprotected.com/mockworkday?tenant_id=${encodeURIComponent(tenantId)}`
    checks.push({
        level: 'info',
        msg: `Mock Workday data URL for this tenant's .env: ${mockUrl}`,
    })

    if (checks.length === 0) return null

    return (
        <div className="sa-mock-preflight">
            {checks.map((c, i) => (
                <div key={i} className={`sa-mock-preflight-item sa-mock-preflight-item--${c.level}`}>
                    <Typography variant="body2">{c.msg}</Typography>
                </div>
            ))}
        </div>
    )
}

export function MockIntegrationPanel({ tenantId }) {
    const confirmRef = useRef()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const [schedule, setSchedule] = useState(null)
    const [tenantLocations, setTenantLocations] = useState([])
    const [datamapEntries, setDatamapEntries] = useState([])
    const [locationmapEntries, setLocationmapEntries] = useState([])
    const [rows, setRows] = useState([])
    const [savedSnapshot, setSavedSnapshot] = useState('')
    const [queueSync, setQueueSync] = useState(true)
    const [importOpen, setImportOpen] = useState(false)
    const [importText, setImportText] = useState('')
    const [importError, setImportError] = useState('')
    const [actionMsg, setActionMsg] = useState('')
    const [saveMsg, setSaveMsg] = useState('')
    const [spidRefreshToken, setSpidRefreshToken] = useState(0)
    const [surveys, setSurveys] = useState([])
    const [selectedSurvey, setSelectedSurvey] = useState('')
    const [assignMsg, setAssignMsg] = useState('')
    const [assigning, setAssigning] = useState(false)

    const locationIds = useMemo(
        () => tenantLocations.map(loc => loc.id).filter(Boolean),
        [tenantLocations]
    )

    const datamap = useMemo(() => mapFromEntries(datamapEntries), [datamapEntries])
    const columns = useMemo(() => datamapKeys(datamap), [datamap])

    const currentSnapshot = useMemo(() => JSON.stringify({
        datamapEntries,
        locationmapEntries,
        rows,
    }), [datamapEntries, locationmapEntries, rows])

    const dirty = savedSnapshot && currentSnapshot !== savedSnapshot

    const configDirty = useMemo(() => {
        if (!savedSnapshot) return false
        try {
            const saved = JSON.parse(savedSnapshot)
            return JSON.stringify(saved.datamapEntries) !== JSON.stringify(datamapEntries)
                || JSON.stringify(saved.locationmapEntries) !== JSON.stringify(locationmapEntries)
        } catch { return false }
    }, [savedSnapshot, datamapEntries, locationmapEntries])

    const mockDataDirty = useMemo(() => {
        if (!savedSnapshot) return false
        try {
            const saved = JSON.parse(savedSnapshot)
            return JSON.stringify(saved.rows) !== JSON.stringify(rows)
        } catch { return false }
    }, [savedSnapshot, rows])

    const locationWarnings = useMemo(() => {
        const warnings = []
        locationmapEntries.forEach(({ key, value }) => {
            if (value && locationIds.length && !locationIds.includes(value)) {
                warnings.push(`"${key}" → "${value}" is not a known tenant location id`)
            }
        })
        return warnings
    }, [locationmapEntries, locationIds])

    const applyPayload = useCallback((data) => {
        const workday = data?.profile?.settings?.integrations?.workday || EMPTY_WORKDAY
        setSchedule(data.profile)
        setTenantLocations(data.tenant_locations || [])
        setDatamapEntries(entriesFromMap(workday.datamap))
        setLocationmapEntries(entriesFromMap(workday.locationmap))
        setRows(Array.isArray(data.mock_data?.report_entry) ? data.mock_data.report_entry : [])
        const snap = JSON.stringify({
            datamapEntries: entriesFromMap(workday.datamap),
            locationmapEntries: entriesFromMap(workday.locationmap),
            rows: Array.isArray(data.mock_data?.report_entry) ? data.mock_data.report_entry : [],
        })
        setSavedSnapshot(snap)
    }, [])

    const load = useCallback(() => {
        setLoading(true)
        setError(null)
        return ADMIN_GET_MOCK_INTEGRATION(tenantId)
            .then(applyPayload)
            .catch(err => setError(getApiErrorMessage(err, 'Failed to load mock integration config')))
            .finally(() => setLoading(false))
    }, [tenantId, applyPayload])

    useEffect(() => { load() }, [load])

    useEffect(() => {
        ADMIN_GET_TENANT_SURVEYS(tenantId)
            .then(result => {
                const items = Array.isArray(result) ? result : []
                setSurveys(items)
                if (items.length > 0 && !selectedSurvey) {
                    setSelectedSurvey(items[0].survey_id || (items[0].sk || '').replace('SURVEY#', '') || '')
                }
            })
            .catch(() => {})
    }, [tenantId])

    const handleAssignRandom = () => {
        setAssigning(true)
        setAssignMsg('')
        ADMIN_GET_TENANT_SPIDS(tenantId)
            .then(spids => {
                const eligible = (Array.isArray(spids) ? spids : []).filter(s =>
                    s.state !== 'Disabled' && !(s.surveys && s.surveys[selectedSurvey])
                )
                if (eligible.length === 0) {
                    setAssignMsg('All employees already have this survey. Add more mock employees to continue testing.')
                    return null
                }
                const pick = eligible[Math.floor(Math.random() * eligible.length)]
                return ADMIN_ASSIGN_SURVEY(tenantId, selectedSurvey, [pick.sp_id])
            })
            .then(res => {
                if (res) setAssignMsg(res.message)
                setSpidRefreshToken(t => t + 1)
            })
            .catch(err => setAssignMsg(getApiErrorMessage(err, 'Could not assign survey.')))
            .finally(() => setAssigning(false))
    }

    const handleBulkAssign = () => {
        setAssigning(true)
        setAssignMsg('')
        ADMIN_GET_TENANT_SPIDS(tenantId)
            .then(spids => {
                const eligible = (Array.isArray(spids) ? spids : []).filter(s =>
                    s.state !== 'Disabled' && !(s.surveys && s.surveys[selectedSurvey])
                )
                if (eligible.length === 0) {
                    setAssignMsg('All employees already have this survey. Add more mock employees to continue testing.')
                    return null
                }
                const shuffled = eligible.sort(() => Math.random() - 0.5)
                const batch = shuffled.slice(0, 5)
                const ids = batch.map(s => s.sp_id)
                if (eligible.length < 5) {
                    setAssignMsg(`Only ${eligible.length} employees without this survey — assigning to all of them.`)
                }
                return ADMIN_ASSIGN_SURVEY(tenantId, selectedSurvey, ids)
            })
            .then(res => {
                if (res?.message) setAssignMsg(prev => prev ? `${prev} ${res.message}` : res.message)
                setSpidRefreshToken(t => t + 1)
            })
            .catch(err => setAssignMsg(getApiErrorMessage(err, 'Could not assign surveys.')))
            .finally(() => setAssigning(false))
    }

    const handleSaveMockData = () => {
        setActionMsg('')
        setSaveMsg('')
        setSaving(true)
        setError(null)
        ADMIN_SAVE_MOCK_DATA(tenantId, rows)
            .then(result => {
                setRows(Array.isArray(result.mock_data?.report_entry) ? result.mock_data.report_entry : rows)
                setSavedSnapshot(JSON.stringify({
                    datamapEntries,
                    locationmapEntries,
                    rows: Array.isArray(result.mock_data?.report_entry) ? result.mock_data.report_entry : rows,
                }))
                const interval = schedule?.integration_interval
                setSaveMsg(interval
                    ? `Mock employees saved. Integration runs every ${interval} — changes will be picked up on the next run.`
                    : 'Mock employees saved. The next integration run will pick up the changes.'
                )
                setTimeout(() => setSpidRefreshToken(t => t + 1), 5000)
            })
            .catch(err => setError(getApiErrorMessage(err, 'Could not save mock employees — the server returned an error. Try reloading the page.')))
            .finally(() => setSaving(false))
    }

    const handleSaveConfig = () => {
        confirmRef.current?.confirm({
            title: 'Save Workday mapping config?',
            message: (
                <>
                    <Typography variant="body2" gutterBottom>
                        This updates the Workday column and location mappings for this tenant.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={queueSync}
                                onChange={e => setQueueSync(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Push config to client app on next heartbeat"
                    />
                </>
            ),
            confirmLabel: 'Save config',
            onConfirm: () => {
                setSaving(true)
                setError(null)
                return ADMIN_SAVE_MOCK_INTEGRATION(tenantId, {
                    profile: {
                        settings: {
                            integrations: {
                                workday: {
                                    datamap: mapFromEntries(datamapEntries),
                                    locationmap: mapFromEntries(locationmapEntries),
                                },
                            },
                        },
                    },
                    mock_data: { report_entry: rows },
                    queue_sync: queueSync,
                })
                    .then(applyPayload)
                    .catch(err => {
                        setError(getApiErrorMessage(err, 'Failed to save integration config'))
                        throw err
                    })
                    .finally(() => setSaving(false))
            },
        })
    }

    const applyImport = () => {
        setImportError('')
        try {
            const parsed = JSON.parse(importText)
            if (!Array.isArray(parsed)) throw new Error('Expected a JSON array')
            setRows(parsed)
            setImportOpen(false)
            setImportText('')
        } catch (e) {
            setImportError(e.message)
        }
    }

    if (loading) {
        return (
            <div className="superadmin-card sa-mock-integration-panel">
                <CircularProgress size={24} />
            </div>
        )
    }

    return (
        <>
            <div className="superadmin-card sa-mock-integration-panel">
                <div className="sa-mock-panel-header">
                    <div>
                        <Typography variant="subtitle1">Mock Integration</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Workday mappings, mock employee data, and reported schedule for signoff / demo tenants.
                        </Typography>
                    </div>
                    <Button variant="outlined" size="small" onClick={load} disabled={saving}>
                        Reload
                    </Button>
                </div>

                {error && (
                    <Typography variant="body2" color="error" className="sa-mock-error">
                        {error}
                    </Typography>
                )}

                <PreflightBanner schedule={schedule} tenantLocations={tenantLocations} tenantId={tenantId} />

                <div className="sa-mock-section">
                    <div className="sa-mock-section-head">
                        <Typography variant="subtitle2">Integration schedule</Typography>
                        <span className="sa-mock-chip sa-mock-chip--readonly">Read-only · from client .env</span>
                    </div>
                    <ScheduleReadOnly schedule={schedule} />
                </div>

                <div className="sa-mock-section">
                    <KeyValueEditor
                        title="Workday column map (datamap)"
                        description="Maps Workday report column names to labels used by the integration job."
                        entries={datamapEntries}
                        onChange={setDatamapEntries}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        className="sa-mock-add-btn"
                        onClick={() => setDatamapEntries([...datamapEntries, { key: 'New_Column', value: 'Label' }])}
                    >
                        + Add mapping
                    </Button>
                </div>

                <div className="sa-mock-section">
                    <KeyValueEditor
                        title="Location map (locationmap)"
                        description="Maps Workday country/region values to tenant location ids."
                        entries={locationmapEntries}
                        onChange={setLocationmapEntries}
                        valueFieldType="location"
                        locationOptions={locationIds}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        className="sa-mock-add-btn"
                        onClick={() => setLocationmapEntries([
                            ...locationmapEntries,
                            { key: 'New Country', value: locationIds[0] || '' },
                        ])}
                    >
                        + Add location
                    </Button>
                    {locationWarnings.length > 0 && (
                        <ul className="sa-mock-warn-list">
                            {locationWarnings.map(w => <li key={w}>{w}</li>)}
                        </ul>
                    )}
                </div>

                <div className="sa-mock-section">
                    <div className="sa-mock-section-head">
                        <Typography variant="subtitle2">Mock Workday employees (Report_Entry)</Typography>
                        <span className="sa-mock-chip">Editable</span>
                        <span className="sa-mock-chip">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
                    </div>
                    <Typography variant="body2" color="textSecondary" className="sa-mock-section-desc">
                        Served by <code>/mockworkday</code> when <code>WORKDAY_DATA_URL</code> points at the mock endpoint.
                    </Typography>
                    <MockRowsEditor rows={rows} columns={columns} onChange={setRows} />
                    <div className="sa-mock-row-actions">
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                const locationKeys = locationmapEntries.map(e => e.key).filter(Boolean)
                                const newRow = generateMockEmployee(columns, rows, locationKeys, datamap)
                                setRows([...rows, newRow])
                                // Find the name and email from the generated row for the feedback message
                                const emailCol = columns.find(c => /email/i.test(c)) || columns.find(c => /email/i.test((datamap || {})[c] || ''))
                                const firstCol = columns.find(c => /first/i.test(c)) || columns.find(c => /first/i.test((datamap || {})[c] || ''))
                                const lastCol = columns.find(c => /last/i.test(c)) || columns.find(c => /last/i.test((datamap || {})[c] || ''))
                                const name = [newRow[firstCol], newRow[lastCol]].filter(Boolean).join(' ') || 'New employee'
                                const email = newRow[emailCol] || ''
                                setActionMsg(`${name} added${email ? ` (${email})` : ''}. Save to push to mock Workday feed.`)
                            }}
                            disabled={columns.length === 0}
                        >
                            + Add employee
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                if (rows.length === 0) {
                                    setActionMsg('No mock employees to remove. Add some first.')
                                    return
                                }
                                const removed = rows[0]
                                const firstCol = columns.find(c => /first/i.test(c)) || columns.find(c => /first/i.test((datamap || {})[c] || ''))
                                const lastCol = columns.find(c => /last/i.test(c)) || columns.find(c => /last/i.test((datamap || {})[c] || ''))
                                const emailCol = columns.find(c => /email/i.test(c)) || columns.find(c => /email/i.test((datamap || {})[c] || ''))
                                const name = [removed[firstCol], removed[lastCol]].filter(Boolean).join(' ') || 'Employee'
                                const addr = removed[emailCol] || ''
                                setRows(rows.slice(1))
                                setActionMsg(`${name} removed from mock data${addr ? ` (${addr})` : ''}. Save to trigger offboarding on next integration run.`)
                            }}
                            disabled={rows.length === 0}
                        >
                            Remove employee
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => setImportOpen(v => !v)}>
                            {importOpen ? 'Hide import' : 'Import JSON'}
                        </Button>
                    </div>
                    {actionMsg && (
                        <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic' }}>{actionMsg}</Typography>
                    )}
                    {importOpen && (
                        <div className="sa-mock-import">
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder='[{"Employee_ID":"1001", ...}]'
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                            />
                            {importError && (
                                <Typography variant="caption" color="error">{importError}</Typography>
                            )}
                            <div className="sa-mock-import-actions">
                                <Button size="small" variant="contained" color="primary" onClick={applyImport}>
                                    Apply import
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="sa-mock-footer">
                    <Typography variant="caption" color="textSecondary">
                        {dirty ? 'Unsaved changes' : 'No unsaved changes'}
                    </Typography>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {mockDataDirty && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleSaveMockData}
                                disabled={saving}
                            >
                                {saving ? <CircularProgress size={16} /> : 'Save mock employees'}
                            </Button>
                        )}
                        {configDirty && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={handleSaveConfig}
                                disabled={saving}
                            >
                                {saving ? <CircularProgress size={16} /> : 'Save mapping config'}
                            </Button>
                        )}
                    </div>
                </div>
                {saveMsg && (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, padding: '0 16px' }}>{saveMsg}</Typography>
                )}
            </div>
            <SpidStatusTable tenantId={tenantId} refreshToken={spidRefreshToken} />
            {surveys.length > 0 && (
                <div className="superadmin-card sa-mock-integration-panel" style={{ marginTop: 16 }}>
                    <div className="sa-mock-section-head">
                        <Typography variant="subtitle2">Survey Assignment</Typography>
                    </div>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                        Assign a survey to test employees. The client will process the assignment on its next heartbeat.
                    </Typography>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                        <TextField
                            select
                            label="Survey"
                            value={selectedSurvey}
                            onChange={e => setSelectedSurvey(e.target.value)}
                            variant="outlined"
                            size="small"
                            style={{ minWidth: 200 }}
                        >
                            {surveys.map(s => {
                                const id = s.survey_id || (s.sk || '').replace('SURVEY#', '') || ''
                                return <MenuItem key={id} value={id}>{s.title || id}</MenuItem>
                            })}
                        </TextField>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAssignRandom}
                            disabled={assigning || !selectedSurvey}
                        >
                            {assigning ? <CircularProgress size={14} /> : 'Assign to 1 random'}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleBulkAssign}
                            disabled={assigning || !selectedSurvey}
                        >
                            {assigning ? <CircularProgress size={14} /> : 'Bulk assign (up to 5)'}
                        </Button>
                    </div>
                    {assignMsg && (
                        <Typography variant="body2" style={{ marginTop: 4 }}>{assignMsg}</Typography>
                    )}
                </div>
            )}
            <ConfirmActionDialog ref={confirmRef} />
        </>
    )
}
